import React, { useCallback, useEffect, useImperativeHandle, useMemo, useRef, forwardRef } from 'react';
import { Text } from '../../../../ui/atoms/Text/Text';
import { InlineText } from '../../../../ui/molecules/inputs/inline/InlineText/InlineText';
import { parsePromptTemplate, getUniqueInputKeys } from '../../utils/parsePromptTemplate';
import './PromptWithInputsStyles.scss';
const BASE_CLASS = 'prompt-with-inputs';
function PromptWithInputsInner({ template, inputValues, onInputValuesChange, onCompletionChange }, ref) {
    const inputWrapperRefs = useRef({});
    const firstInputKeyRef = useRef(null);
    const onCompletionChangeRef = useRef(onCompletionChange);
    onCompletionChangeRef.current = onCompletionChange;
    const inputValuesRef = useRef(inputValues);
    inputValuesRef.current = inputValues;
    const segments = useMemo(() => parsePromptTemplate(template), [template]);
    const uniqueKeys = useMemo(() => getUniqueInputKeys(segments), [segments]);
    useEffect(() => {
        firstInputKeyRef.current = uniqueKeys.length > 0 ? uniqueKeys[0] : null;
    }, [uniqueKeys]);
    const allFilled = useMemo(() => {
        if (uniqueKeys.length === 0)
            return true;
        return uniqueKeys.every((key) => (inputValues[key] || '').trim().length > 0);
    }, [uniqueKeys, inputValues]);
    useEffect(() => {
        onCompletionChangeRef.current(allFilled);
    }, [allFilled]);
    const onInputValuesChangeRef = useRef(onInputValuesChange);
    onInputValuesChangeRef.current = onInputValuesChange;
    const handleInput = useCallback((key, event) => {
        const value = event.currentTarget.textContent || '';
        const newValues = Object.assign(Object.assign({}, inputValuesRef.current), { [key]: value });
        onInputValuesChangeRef.current(newValues);
    }, []);
    const getAssembledPrompt = useCallback(() => {
        let result = template;
        uniqueKeys.forEach((key) => {
            const value = (inputValues[key] || '').trim();
            const escaped = key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`\\[${escaped}\\]`, 'g');
            result = result.replace(regex, value || `[${key}]`);
        });
        return result;
    }, [template, uniqueKeys, inputValues]);
    const focusFirstInput = useCallback(() => {
        const firstKey = firstInputKeyRef.current;
        if (!firstKey)
            return;
        const wrapper = inputWrapperRefs.current[firstKey];
        const editable = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('[contenteditable="true"]');
        editable === null || editable === void 0 ? void 0 : editable.focus();
    }, []);
    useImperativeHandle(ref, () => ({ getAssembledPrompt, focusFirstInput }), [getAssembledPrompt, focusFirstInput]);
    const setWrapperRef = useCallback((key, el) => {
        inputWrapperRefs.current[key] = el;
    }, []);
    const renderedKeys = new Set();
    return (React.createElement("div", { className: BASE_CLASS },
        React.createElement(Text, { variant: "body", as: "span" }, "\u201C"),
        segments.map((segment, index) => {
            if (segment.type === 'text') {
                return (React.createElement(Text, { key: `text-${index}`, variant: "body", as: "span" }, segment.value));
            }
            const isFirstOccurrence = !renderedKeys.has(segment.key);
            renderedKeys.add(segment.key);
            if (!isFirstOccurrence) {
                const displayValue = (inputValues[segment.key] || '').trim();
                return (React.createElement(Text, { key: `shared-${index}`, variant: "body", as: "span", className: `${BASE_CLASS}__shared-value` }, displayValue || segment.label));
            }
            return (React.createElement("span", { key: `input-${index}`, className: `${BASE_CLASS}__input-wrapper`, ref: (el) => setWrapperRef(segment.key, el) },
                React.createElement(InlineText, { placeholder: segment.label, onInput: (e) => handleInput(segment.key, e), className: `${BASE_CLASS}__inline-input` })));
        }),
        React.createElement(Text, { variant: "body", as: "span" }, "\u201D")));
}
export const PromptWithInputs = forwardRef(PromptWithInputsInner);
